/**
 * SB-8809 — widget-chrome dictionaries. English is the reference; de/fr/es
 * cover the initial launch languages (Messe München / Canny request). A
 * missing key in any locale falls back to English at lookup time, so partial
 * dictionaries degrade gracefully rather than rendering blank chrome.
 *
 * Values may contain {placeholders} interpolated by createTranslator.
 * {count}-bearing messages come in `_one` / `_other` pairs picked by the
 * translator's plural helper (en/de/fr/es all use the one/other split for
 * cardinals — fr treats 0/1 as "one", handled in the translator).
 */

export const en = {
  // Loading / errors
  loading_data: 'Loading data...',
  error_loading: 'Error loading data:',
  widget_disabled: 'This widget is disabled',

  // Header / footer
  event: 'Event',
  powered_by: 'Powered by',

  // Search + filters
  filters: 'Filters',
  apply_filters: 'Apply filters',
  clear_filters: 'Clear filters',
  clear_all_filters: 'Clear all filters',
  close_filters: 'Close filters and discard changes',
  clear_search: 'Clear search input',
  search_placeholder_speakers_sessions: 'Search by speaker details or session title',
  search_placeholder_speaker_name: 'Search speaker by name',
  search_placeholder_generic: 'Search...',

  // Result banners (plural pairs)
  no_items_with_title: 'No {items} found with title containing "{query}"',
  showing_zero_match_filters: 'Showing 0 {items} that match filters',
  showing_with_title_one: 'Showing {count} {items} with title "{query}"',
  showing_with_title_other: 'Showing {count} {items} with title "{query}"',
  showing_match_filters_one: 'Showing {count} {items} that match filters',
  showing_match_filters_other: 'Showing {count} {items} that match filters',

  // No results
  no_items_found: 'No {items} found',
  no_results_for: 'No results for "{query}"',
  try_adjusting_filters: 'Try adjusting your filters',

  // Item nouns (interpolated into banners above)
  items_sessions: 'sessions',
  item_session: 'session',
  items_speakers: 'speakers',
  item_speaker: 'speaker',
  items_results: 'results',

  // Pagination
  pagination_range: '{start} - {end} of {total}',
  pagination_sessions: 'Sessions',
  pagination_speakers: 'Speakers',
  previous_page: 'Previous page',
  next_page: 'Next page',

  // Modals (shared)
  back: 'Back',
  go_back: 'Go back',
  close_modal: 'Close modal',
  show_more: 'Show more',
  show_less: 'Show less',
  view_details: 'View Details',
  show_details: 'Show Details',
  hide_details: 'Hide Details',

  // Session modal
  session_details: 'Session Details',
  subsessions_tab: 'Subsessions ({count})',
  ceu_credit_hours: 'CEU Credit Hours:',
  sponsored_by: 'Sponsored by',
  visit_sponsor: 'Visit {name}',
  visit_sponsor_generic: 'Visit sponsor website',
  add_to_calendar: 'Add to Calendar',

  // Speaker modal
  sessions_heading: 'Sessions ({count})',
  linkedin_profile: 'LinkedIn profile',
  x_profile: 'X profile',
  facebook_profile: 'Facebook profile',
  website: 'Website',
  email: 'Email',

  // Role group headings (SB-8853 fallbacks when the payload has no
  // organizer label for a slug)
  role_speakers: 'Speakers',
  role_moderators: 'Moderators',
  role_chairpersons: 'Chairpersons',
  role_speaker: 'Speaker',
  role_moderator: 'Moderator',
  role_chairperson: 'Chairperson',

  // Schedule / agenda chrome
  unassigned: 'Unassigned',
  all_days: 'All days',

  roles_label: 'Roles:',

  // Schedule chrome
  itinerary: 'Itinerary',
  no_sessions_available: 'No Sessions available',

  // Rooms view chrome (SB-9570)
  rooms: 'Rooms',
  capacity: 'Capacity {count}',

  // Schedule tag row labels
  tag_format: 'FORMAT:',
  tag_language: 'LANGUAGE:',
  tag_level: 'LEVEL:',
  tag_track: 'TRACK:',
  tag_tag: 'TAGS:',

  // Content gallery widget (SB-9273)
  cg_search_placeholder: 'Search the gallery',
  cg_search_label: 'Search',
  cg_sort_label: 'Sort by',
  cg_sort_newest: 'Newest',
  cg_sort_oldest: 'Oldest',
  cg_sort_title: 'Title',
  cg_sort_popular: 'Most popular',
  cg_sort_curated: 'Curated',
  cg_sort_schedule: 'Schedule',
  cg_sort_relevance: 'Relevance',
  cg_filters: 'Filters',
  cg_clear_filters: 'Clear filters',
  cg_results_one: '{count} item',
  cg_results_other: '{count} items',
  cg_no_results: 'Nothing matches your search',
  cg_items: 'Items',
  cg_locked: 'Locked',
  cg_featured: 'Featured',
  cg_open_item: 'Open {title}',
  cg_more_options: 'Show {count} more',
  cg_fewer_options: 'Show fewer',
  cg_watch: 'Watch',
  cg_listen: 'Listen',
  cg_open: 'Open',
  cg_download: 'Download',
  cg_open_link: 'Open link',
  cg_show_transcript: 'Show transcript',
  cg_hide_transcript: 'Hide transcript',
  cg_related: 'Related',
  cg_speakers: 'Speakers',
  cg_recording: 'Recording',
  cg_in_progress: '{percent}% watched',
  cg_gate_title: 'Enter your email to continue',
  cg_gate_intro: 'This content is available after you share your email.',
  cg_gate_domains_hint: 'Available to {domains} addresses',
  cg_email: 'Email',
  cg_first_name: 'First name',
  cg_last_name: 'Last name',
  cg_company: 'Company',
  cg_continue: 'Continue',
  cg_login_required: 'Sign in to view this content.',
  cg_sso_required: 'Sign in with your organization to view this content.',
  cg_share_required: 'This content is only available through a share link.',
  cg_private: 'This content is not available.',
  cg_embargoed: 'Available {date}.',
  cg_embargoed_soon: 'Not yet available.',
  cg_go_to_gallery: 'Open in the gallery',
  cg_not_found: 'No content gallery is published for this site.',
  cg_kind_video: 'Video',
  cg_kind_clip: 'Clip',
  cg_kind_podcast: 'Podcast',
  cg_kind_article: 'Article',
  cg_kind_whitepaper: 'White paper',
  cg_kind_document: 'Document',
  cg_kind_transcript: 'Transcript',
  cg_kind_session: 'Session',
  cg_kind_lesson: 'Lesson',
  cg_kind_link: 'Link',

  // SB-9832 — public disclosure slot on speaker cards (entity + nature only)
  disclosure_none: 'No financial relationships disclosed',
  disclosure_count_one: '{count} financial relationship disclosed',
  disclosure_count_other: '{count} financial relationships disclosed',
  disclosure_list_label: 'Disclosed financial relationships',

  // Advocacy "I'm attending" card + open-link signup (advocacy-attending)
  aa_loading: 'Loading your share card…',
  aa_event_fallback: 'the event',
  aa_title_named: "{name}, you're going to {event}",
  aa_title: "You're going to {event}",
  aa_subtitle: 'Let your network know — share your card.',
  aa_image_alt_named: 'Share card: {name} is attending {event}',
  aa_image_alt: 'Share card: attending {event}',
  aa_share_group: 'Share your card',
  aa_new_tab: '(opens in a new tab)',
  aa_share_subject: "I'm attending {event}",
  aa_channel_email: 'Email',
  aa_copy_link: 'Copy link',
  aa_copied: 'Copied',
  aa_copied_announce: 'Link copied to the clipboard.',
  aa_copy_failed: 'Could not copy the link. Select it and copy it instead: {url}',
  aa_make_it_yours: 'Make it yours',
  aa_make_it_yours_hint: 'Add your photo and pick your card',
  aa_unavailable_registration: 'Your share card is not available for this registration.',
  aa_unavailable_event: "Share cards aren't available for this event right now.",
  aa_error_reload: 'Your share card could not be loaded. Please refresh the page.',
  aa_signup_title: "Tell your network you're going to {event}",
  aa_signup_intro: 'Make your personal share card in a few seconds.',
  aa_required_note: 'Fields marked * are required.',
  aa_required: 'required',
  aa_field_first_name: 'First name',
  aa_field_last_name: 'Last name',
  aa_field_email: 'Email',
  aa_field_company: 'Company',
  aa_field_job_title: 'Job title',
  aa_consent: 'I agree to receive my share link and updates about {event}.',
  aa_submit: 'Make my card',
  aa_submitting: 'Making your card…',
  aa_err_first_name: 'Enter your first name.',
  aa_err_email: 'Enter a valid email address, like name@example.com.',
  aa_err_consent: 'Tick the box to get your share card.',
  aa_err_summary_one: 'There is {count} problem with the form.',
  aa_err_summary_other: 'There are {count} problems with the form.',
  aa_err_generic: 'Something went wrong. Please try again.',
  aa_check_email_title: 'Check your inbox',
  aa_check_email_body: 'If {email} already has a share card for {event}, we have sent the personal link there.',
  aa_card_ready: 'Your share card is ready.',
  aa_preview_label: 'Preview of your share card',
};

export const de = {
  loading_data: 'Daten werden geladen...',
  error_loading: 'Fehler beim Laden der Daten:',
  widget_disabled: 'Dieses Widget ist deaktiviert',

  event: 'Veranstaltung',
  powered_by: 'Bereitgestellt von',

  filters: 'Filter',
  apply_filters: 'Filter anwenden',
  clear_filters: 'Filter zurücksetzen',
  clear_all_filters: 'Alle Filter zurücksetzen',
  close_filters: 'Filter schließen und Änderungen verwerfen',
  clear_search: 'Sucheingabe löschen',
  search_placeholder_speakers_sessions: 'Nach Referentendetails oder Sessiontitel suchen',
  search_placeholder_speaker_name: 'Referent nach Namen suchen',
  search_placeholder_generic: 'Suchen...',

  no_items_with_title: 'Keine {items} mit Titel "{query}" gefunden',
  showing_zero_match_filters: '0 {items} entsprechen den Filtern',
  showing_with_title_one: '{count} {items} mit Titel "{query}"',
  showing_with_title_other: '{count} {items} mit Titel "{query}"',
  showing_match_filters_one: '{count} {items} entspricht den Filtern',
  showing_match_filters_other: '{count} {items} entsprechen den Filtern',

  no_items_found: 'Keine {items} gefunden',
  no_results_for: 'Keine Ergebnisse für "{query}"',
  try_adjusting_filters: 'Passen Sie Ihre Filter an',

  items_sessions: 'Sessions',
  item_session: 'Session',
  items_speakers: 'Referenten',
  item_speaker: 'Referent',
  items_results: 'Ergebnisse',

  pagination_range: '{start} - {end} von {total}',
  pagination_sessions: 'Sessions',
  pagination_speakers: 'Referenten',
  previous_page: 'Vorherige Seite',
  next_page: 'Nächste Seite',

  back: 'Zurück',
  go_back: 'Zurück',
  close_modal: 'Fenster schließen',
  show_more: 'Mehr anzeigen',
  show_less: 'Weniger anzeigen',
  view_details: 'Details anzeigen',
  show_details: 'Details anzeigen',
  hide_details: 'Details ausblenden',

  session_details: 'Sessiondetails',
  subsessions_tab: 'Untersessions ({count})',
  ceu_credit_hours: 'CEU-Stunden:',
  sponsored_by: 'Gesponsert von',
  visit_sponsor: '{name} besuchen',
  visit_sponsor_generic: 'Sponsor-Website besuchen',
  add_to_calendar: 'Zum Kalender hinzufügen',

  sessions_heading: 'Sessions ({count})',
  linkedin_profile: 'LinkedIn-Profil',
  x_profile: 'X-Profil',
  facebook_profile: 'Facebook-Profil',
  website: 'Website',
  email: 'E-Mail',

  role_speakers: 'Referenten',
  role_moderators: 'Moderatoren',
  role_chairpersons: 'Vorsitzende',
  role_speaker: 'Referent',
  role_moderator: 'Moderator',
  role_chairperson: 'Vorsitz',

  unassigned: 'Nicht zugewiesen',
  all_days: 'Alle Tage',

  roles_label: 'Rollen:',

  itinerary: 'Programm',
  no_sessions_available: 'Keine Sessions verfügbar',

  rooms: 'Räume',
  capacity: 'Kapazität {count}',

  tag_format: 'FORMAT:',
  tag_language: 'SPRACHE:',
  tag_level: 'NIVEAU:',
  tag_track: 'TRACK:',
  tag_tag: 'TAGS:',

  // Content gallery widget (SB-9273)
  cg_search_placeholder: 'Galerie durchsuchen',
  cg_search_label: 'Suche',
  cg_sort_label: 'Sortieren nach',
  cg_sort_newest: 'Neueste',
  cg_sort_oldest: 'Älteste',
  cg_sort_title: 'Titel',
  cg_sort_popular: 'Beliebteste',
  cg_sort_curated: 'Kuratiert',
  cg_sort_schedule: 'Programm',
  cg_sort_relevance: 'Relevanz',
  cg_filters: 'Filter',
  cg_clear_filters: 'Filter zurücksetzen',
  cg_results_one: '{count} Inhalt',
  cg_results_other: '{count} Inhalte',
  cg_no_results: 'Keine Treffer für Ihre Suche',
  cg_items: 'Inhalte',
  cg_locked: 'Gesperrt',
  cg_featured: 'Empfohlen',
  cg_open_item: '{title} öffnen',
  cg_more_options: '{count} weitere anzeigen',
  cg_fewer_options: 'Weniger anzeigen',
  cg_watch: 'Ansehen',
  cg_listen: 'Anhören',
  cg_open: 'Öffnen',
  cg_download: 'Herunterladen',
  cg_open_link: 'Link öffnen',
  cg_show_transcript: 'Transkript anzeigen',
  cg_hide_transcript: 'Transkript ausblenden',
  cg_related: 'Ähnliche Inhalte',
  cg_speakers: 'Referenten',
  cg_recording: 'Aufzeichnung',
  cg_in_progress: '{percent} % angesehen',
  cg_gate_title: 'Geben Sie Ihre E-Mail-Adresse ein, um fortzufahren',
  cg_gate_intro: 'Dieser Inhalt ist verfügbar, nachdem Sie Ihre E-Mail-Adresse angegeben haben.',
  cg_gate_domains_hint: 'Verfügbar für Adressen von {domains}',
  cg_email: 'E-Mail',
  cg_first_name: 'Vorname',
  cg_last_name: 'Nachname',
  cg_company: 'Unternehmen',
  cg_continue: 'Weiter',
  cg_login_required: 'Melden Sie sich an, um diesen Inhalt zu sehen.',
  cg_sso_required: 'Melden Sie sich über Ihre Organisation an, um diesen Inhalt zu sehen.',
  cg_share_required: 'Dieser Inhalt ist nur über einen Freigabelink verfügbar.',
  cg_private: 'Dieser Inhalt ist nicht verfügbar.',
  cg_embargoed: 'Verfügbar ab {date}.',
  cg_embargoed_soon: 'Noch nicht verfügbar.',
  cg_go_to_gallery: 'In der Galerie öffnen',
  cg_not_found: 'Für diese Website ist keine Inhaltsgalerie veröffentlicht.',
  cg_kind_video: 'Video',
  cg_kind_clip: 'Clip',
  cg_kind_podcast: 'Podcast',
  cg_kind_article: 'Artikel',
  cg_kind_whitepaper: 'Whitepaper',
  cg_kind_document: 'Dokument',
  cg_kind_transcript: 'Transkript',
  cg_kind_session: 'Session',
  cg_kind_lesson: 'Lektion',
  cg_kind_link: 'Link',

  disclosure_none: 'Keine finanziellen Beziehungen offengelegt',
  disclosure_count_one: '{count} finanzielle Beziehung offengelegt',
  disclosure_count_other: '{count} finanzielle Beziehungen offengelegt',
  disclosure_list_label: 'Offengelegte finanzielle Beziehungen',

  // Advocacy "I'm attending" card + open-link signup (advocacy-attending)
  aa_loading: 'Ihre Teilen-Karte wird geladen…',
  aa_event_fallback: 'die Veranstaltung',
  aa_title_named: '{name}, Sie sind bei {event} dabei',
  aa_title: 'Sie sind bei {event} dabei',
  aa_subtitle: 'Sagen Sie es Ihrem Netzwerk – teilen Sie Ihre Karte.',
  aa_image_alt_named: 'Teilen-Karte: {name} nimmt an {event} teil',
  aa_image_alt: 'Teilen-Karte: Teilnahme an {event}',
  aa_share_group: 'Ihre Karte teilen',
  aa_new_tab: '(öffnet in einem neuen Tab)',
  aa_share_subject: 'Ich nehme an {event} teil',
  aa_channel_email: 'E-Mail',
  aa_copy_link: 'Link kopieren',
  aa_copied: 'Kopiert',
  aa_copied_announce: 'Link in die Zwischenablage kopiert.',
  aa_copy_failed: 'Der Link konnte nicht kopiert werden. Markieren und kopieren Sie ihn: {url}',
  aa_make_it_yours: 'Personalisieren',
  aa_make_it_yours_hint: 'Foto hinzufügen und Karte auswählen',
  aa_unavailable_registration: 'Ihre Teilen-Karte ist für diese Anmeldung nicht verfügbar.',
  aa_unavailable_event: 'Teilen-Karten sind für diese Veranstaltung derzeit nicht verfügbar.',
  aa_error_reload: 'Ihre Teilen-Karte konnte nicht geladen werden. Bitte laden Sie die Seite neu.',
  aa_signup_title: 'Sagen Sie Ihrem Netzwerk, dass Sie bei {event} dabei sind',
  aa_signup_intro: 'Erstellen Sie Ihre persönliche Teilen-Karte in wenigen Sekunden.',
  aa_required_note: 'Mit * gekennzeichnete Felder sind Pflichtfelder.',
  aa_required: 'Pflichtfeld',
  aa_field_first_name: 'Vorname',
  aa_field_last_name: 'Nachname',
  aa_field_email: 'E-Mail',
  aa_field_company: 'Unternehmen',
  aa_field_job_title: 'Position',
  aa_consent: 'Ich möchte meinen Teilen-Link und Neuigkeiten zu {event} erhalten.',
  aa_submit: 'Meine Karte erstellen',
  aa_submitting: 'Ihre Karte wird erstellt…',
  aa_err_first_name: 'Geben Sie Ihren Vornamen ein.',
  aa_err_email: 'Geben Sie eine gültige E-Mail-Adresse ein, z. B. name@example.com.',
  aa_err_consent: 'Setzen Sie das Häkchen, um Ihre Teilen-Karte zu erhalten.',
  aa_err_summary_one: 'Im Formular gibt es {count} Problem.',
  aa_err_summary_other: 'Im Formular gibt es {count} Probleme.',
  aa_err_generic: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
  aa_check_email_title: 'Prüfen Sie Ihren Posteingang',
  aa_check_email_body:
    'Falls {email} bereits eine Teilen-Karte für {event} hat, haben wir den persönlichen Link dorthin gesendet.',
  aa_card_ready: 'Ihre Teilen-Karte ist fertig.',
  aa_preview_label: 'Vorschau Ihrer Teilen-Karte',
};

export const fr = {
  loading_data: 'Chargement des données...',
  error_loading: 'Erreur lors du chargement des données :',
  widget_disabled: 'Ce widget est désactivé',

  event: 'Événement',
  powered_by: 'Propulsé par',

  filters: 'Filtres',
  apply_filters: 'Appliquer les filtres',
  clear_filters: 'Effacer les filtres',
  clear_all_filters: 'Effacer tous les filtres',
  close_filters: 'Fermer les filtres et annuler les modifications',
  clear_search: 'Effacer la recherche',
  search_placeholder_speakers_sessions: 'Rechercher par intervenant ou titre de session',
  search_placeholder_speaker_name: 'Rechercher un intervenant par nom',
  search_placeholder_generic: 'Rechercher...',

  no_items_with_title: 'Aucune {items} trouvée avec le titre "{query}"',
  showing_zero_match_filters: '0 {items} ne correspondent aux filtres',
  showing_with_title_one: '{count} {items} avec le titre "{query}"',
  showing_with_title_other: '{count} {items} avec le titre "{query}"',
  showing_match_filters_one: '{count} {items} correspond aux filtres',
  showing_match_filters_other: '{count} {items} correspondent aux filtres',

  no_items_found: 'Aucun résultat pour {items}',
  no_results_for: 'Aucun résultat pour "{query}"',
  try_adjusting_filters: 'Essayez de modifier vos filtres',

  items_sessions: 'sessions',
  item_session: 'session',
  items_speakers: 'intervenants',
  item_speaker: 'intervenant',
  items_results: 'résultats',

  pagination_range: '{start} - {end} sur {total}',
  pagination_sessions: 'Sessions',
  pagination_speakers: 'Intervenants',
  previous_page: 'Page précédente',
  next_page: 'Page suivante',

  back: 'Retour',
  go_back: 'Retour',
  close_modal: 'Fermer la fenêtre',
  show_more: 'Voir plus',
  show_less: 'Voir moins',
  view_details: 'Voir les détails',
  show_details: 'Afficher les détails',
  hide_details: 'Masquer les détails',

  session_details: 'Détails de la session',
  subsessions_tab: 'Sous-sessions ({count})',
  ceu_credit_hours: 'Heures de crédit CEU :',
  sponsored_by: 'Sponsorisé par',
  visit_sponsor: 'Visiter {name}',
  visit_sponsor_generic: 'Visiter le site du sponsor',
  add_to_calendar: 'Ajouter au calendrier',

  sessions_heading: 'Sessions ({count})',
  linkedin_profile: 'Profil LinkedIn',
  x_profile: 'Profil X',
  facebook_profile: 'Profil Facebook',
  website: 'Site web',
  email: 'E-mail',

  role_speakers: 'Intervenants',
  role_moderators: 'Modérateurs',
  role_chairpersons: 'Présidents',
  role_speaker: 'Intervenant',
  role_moderator: 'Modérateur',
  role_chairperson: 'Président',

  unassigned: 'Non attribué',
  all_days: 'Tous les jours',

  roles_label: 'Rôles :',

  itinerary: 'Programme',
  no_sessions_available: 'Aucune session disponible',

  rooms: 'Salles',
  capacity: 'Capacité {count}',

  tag_format: 'FORMAT :',
  tag_language: 'LANGUE :',
  tag_level: 'NIVEAU :',
  tag_track: 'THÈME :',
  tag_tag: 'TAGS :',

  // Content gallery widget (SB-9273)
  cg_search_placeholder: 'Rechercher dans la galerie',
  cg_search_label: 'Rechercher',
  cg_sort_label: 'Trier par',
  cg_sort_newest: 'Plus récents',
  cg_sort_oldest: 'Plus anciens',
  cg_sort_title: 'Titre',
  cg_sort_popular: 'Les plus populaires',
  cg_sort_curated: 'Sélection',
  cg_sort_schedule: 'Programme',
  cg_sort_relevance: 'Pertinence',
  cg_filters: 'Filtres',
  cg_clear_filters: 'Effacer les filtres',
  cg_results_one: '{count} contenu',
  cg_results_other: '{count} contenus',
  cg_no_results: 'Aucun résultat pour votre recherche',
  cg_items: 'Contenus',
  cg_locked: 'Verrouillé',
  cg_featured: 'À la une',
  cg_open_item: 'Ouvrir {title}',
  cg_more_options: 'Afficher {count} de plus',
  cg_fewer_options: 'Afficher moins',
  cg_watch: 'Regarder',
  cg_listen: 'Écouter',
  cg_open: 'Ouvrir',
  cg_download: 'Télécharger',
  cg_open_link: 'Ouvrir le lien',
  cg_show_transcript: 'Afficher la transcription',
  cg_hide_transcript: 'Masquer la transcription',
  cg_related: 'Contenus associés',
  cg_speakers: 'Intervenants',
  cg_recording: 'Enregistrement',
  cg_in_progress: '{percent} % visionné',
  cg_gate_title: 'Saisissez votre e-mail pour continuer',
  cg_gate_intro: 'Ce contenu est disponible après avoir indiqué votre e-mail.',
  cg_gate_domains_hint: 'Réservé aux adresses {domains}',
  cg_email: 'E-mail',
  cg_first_name: 'Prénom',
  cg_last_name: 'Nom',
  cg_company: 'Entreprise',
  cg_continue: 'Continuer',
  cg_login_required: 'Connectez-vous pour voir ce contenu.',
  cg_sso_required: 'Connectez-vous via votre organisation pour voir ce contenu.',
  cg_share_required: 'Ce contenu est uniquement accessible via un lien de partage.',
  cg_private: 'Ce contenu n’est pas disponible.',
  cg_embargoed: 'Disponible le {date}.',
  cg_embargoed_soon: 'Pas encore disponible.',
  cg_go_to_gallery: 'Ouvrir dans la galerie',
  cg_not_found: 'Aucune galerie de contenus n’est publiée pour ce site.',
  cg_kind_video: 'Vidéo',
  cg_kind_clip: 'Extrait',
  cg_kind_podcast: 'Podcast',
  cg_kind_article: 'Article',
  cg_kind_whitepaper: 'Livre blanc',
  cg_kind_document: 'Document',
  cg_kind_transcript: 'Transcription',
  cg_kind_session: 'Session',
  cg_kind_lesson: 'Leçon',
  cg_kind_link: 'Lien',

  disclosure_none: 'Aucune relation financière déclarée',
  disclosure_count_one: '{count} relation financière déclarée',
  disclosure_count_other: '{count} relations financières déclarées',
  disclosure_list_label: 'Relations financières déclarées',

  // Advocacy "I'm attending" card + open-link signup (advocacy-attending)
  aa_loading: 'Chargement de votre carte de partage…',
  aa_event_fallback: "l'événement",
  aa_title_named: '{name}, vous participez à {event}',
  aa_title: 'Vous participez à {event}',
  aa_subtitle: 'Faites-le savoir à votre réseau : partagez votre carte.',
  aa_image_alt_named: 'Carte de partage : {name} participe à {event}',
  aa_image_alt: 'Carte de partage : participation à {event}',
  aa_share_group: 'Partager votre carte',
  aa_new_tab: '(ouvre un nouvel onglet)',
  aa_share_subject: 'Je participe à {event}',
  aa_channel_email: 'E-mail',
  aa_copy_link: 'Copier le lien',
  aa_copied: 'Copié',
  aa_copied_announce: 'Lien copié dans le presse-papiers.',
  aa_copy_failed: 'Impossible de copier le lien. Sélectionnez-le et copiez-le : {url}',
  aa_make_it_yours: 'Personnaliser',
  aa_make_it_yours_hint: 'Ajoutez votre photo et choisissez votre carte',
  aa_unavailable_registration: "Votre carte de partage n'est pas disponible pour cette inscription.",
  aa_unavailable_event: 'Les cartes de partage ne sont pas disponibles pour cet événement pour le moment.',
  aa_error_reload: "Votre carte de partage n'a pas pu être chargée. Veuillez actualiser la page.",
  aa_signup_title: 'Dites à votre réseau que vous participez à {event}',
  aa_signup_intro: 'Créez votre carte de partage personnelle en quelques secondes.',
  aa_required_note: 'Les champs marqués d’un * sont obligatoires.',
  aa_required: 'obligatoire',
  aa_field_first_name: 'Prénom',
  aa_field_last_name: 'Nom',
  aa_field_email: 'E-mail',
  aa_field_company: 'Entreprise',
  aa_field_job_title: 'Fonction',
  aa_consent: "J'accepte de recevoir mon lien de partage et des actualités sur {event}.",
  aa_submit: 'Créer ma carte',
  aa_submitting: 'Création de votre carte…',
  aa_err_first_name: 'Saisissez votre prénom.',
  aa_err_email: 'Saisissez une adresse e-mail valide, par exemple nom@example.com.',
  aa_err_consent: 'Cochez la case pour obtenir votre carte de partage.',
  aa_err_summary_one: 'Le formulaire contient {count} erreur.',
  aa_err_summary_other: 'Le formulaire contient {count} erreurs.',
  aa_err_generic: 'Un problème est survenu. Veuillez réessayer.',
  aa_check_email_title: 'Consultez votre boîte de réception',
  aa_check_email_body:
    'Si {email} possède déjà une carte de partage pour {event}, nous y avons envoyé le lien personnel.',
  aa_card_ready: 'Votre carte de partage est prête.',
  aa_preview_label: 'Aperçu de votre carte de partage',
};

export const es = {
  loading_data: 'Cargando datos...',
  error_loading: 'Error al cargar los datos:',
  widget_disabled: 'Este widget está desactivado',

  event: 'Evento',
  powered_by: 'Desarrollado por',

  filters: 'Filtros',
  apply_filters: 'Aplicar filtros',
  clear_filters: 'Borrar filtros',
  clear_all_filters: 'Borrar todos los filtros',
  close_filters: 'Cerrar filtros y descartar cambios',
  clear_search: 'Borrar búsqueda',
  search_placeholder_speakers_sessions: 'Buscar por ponente o título de la sesión',
  search_placeholder_speaker_name: 'Buscar ponente por nombre',
  search_placeholder_generic: 'Buscar...',

  no_items_with_title: 'No se encontraron {items} con el título "{query}"',
  showing_zero_match_filters: '0 {items} coinciden con los filtros',
  showing_with_title_one: 'Mostrando {count} {items} con el título "{query}"',
  showing_with_title_other: 'Mostrando {count} {items} con el título "{query}"',
  showing_match_filters_one: 'Mostrando {count} {items} que coincide con los filtros',
  showing_match_filters_other: 'Mostrando {count} {items} que coinciden con los filtros',

  no_items_found: 'No se encontraron {items}',
  no_results_for: 'Sin resultados para "{query}"',
  try_adjusting_filters: 'Prueba a ajustar los filtros',

  items_sessions: 'sesiones',
  item_session: 'sesión',
  items_speakers: 'ponentes',
  item_speaker: 'ponente',
  items_results: 'resultados',

  pagination_range: '{start} - {end} de {total}',
  pagination_sessions: 'Sesiones',
  pagination_speakers: 'Ponentes',
  previous_page: 'Página anterior',
  next_page: 'Página siguiente',

  back: 'Atrás',
  go_back: 'Volver',
  close_modal: 'Cerrar ventana',
  show_more: 'Mostrar más',
  show_less: 'Mostrar menos',
  view_details: 'Ver detalles',
  show_details: 'Mostrar detalles',
  hide_details: 'Ocultar detalles',

  session_details: 'Detalles de la sesión',
  subsessions_tab: 'Subsesiones ({count})',
  ceu_credit_hours: 'Horas de crédito CEU:',
  sponsored_by: 'Patrocinado por',
  visit_sponsor: 'Visitar {name}',
  visit_sponsor_generic: 'Visitar el sitio del patrocinador',
  add_to_calendar: 'Añadir al calendario',

  sessions_heading: 'Sesiones ({count})',
  linkedin_profile: 'Perfil de LinkedIn',
  x_profile: 'Perfil de X',
  facebook_profile: 'Perfil de Facebook',
  website: 'Sitio web',
  email: 'Correo electrónico',

  role_speakers: 'Ponentes',
  role_moderators: 'Moderadores',
  role_chairpersons: 'Presidentes',
  role_speaker: 'Ponente',
  role_moderator: 'Moderador',
  role_chairperson: 'Presidente',

  unassigned: 'Sin asignar',
  all_days: 'Todos los días',

  roles_label: 'Roles:',

  itinerary: 'Itinerario',
  no_sessions_available: 'No hay sesiones disponibles',

  rooms: 'Salas',
  capacity: 'Capacidad {count}',

  tag_format: 'FORMATO:',
  tag_language: 'IDIOMA:',
  tag_level: 'NIVEL:',
  tag_track: 'TEMA:',
  tag_tag: 'ETIQUETAS:',

  // Content gallery widget (SB-9273)
  cg_search_placeholder: 'Buscar en la galería',
  cg_search_label: 'Buscar',
  cg_sort_label: 'Ordenar por',
  cg_sort_newest: 'Más recientes',
  cg_sort_oldest: 'Más antiguos',
  cg_sort_title: 'Título',
  cg_sort_popular: 'Más populares',
  cg_sort_curated: 'Selección',
  cg_sort_schedule: 'Programa',
  cg_sort_relevance: 'Relevancia',
  cg_filters: 'Filtros',
  cg_clear_filters: 'Borrar filtros',
  cg_results_one: '{count} contenido',
  cg_results_other: '{count} contenidos',
  cg_no_results: 'No hay resultados para tu búsqueda',
  cg_items: 'Contenidos',
  cg_locked: 'Bloqueado',
  cg_featured: 'Destacado',
  cg_open_item: 'Abrir {title}',
  cg_more_options: 'Mostrar {count} más',
  cg_fewer_options: 'Mostrar menos',
  cg_watch: 'Ver',
  cg_listen: 'Escuchar',
  cg_open: 'Abrir',
  cg_download: 'Descargar',
  cg_open_link: 'Abrir enlace',
  cg_show_transcript: 'Mostrar transcripción',
  cg_hide_transcript: 'Ocultar transcripción',
  cg_related: 'Relacionados',
  cg_speakers: 'Ponentes',
  cg_recording: 'Grabación',
  cg_in_progress: '{percent} % visto',
  cg_gate_title: 'Introduce tu correo electrónico para continuar',
  cg_gate_intro: 'Este contenido está disponible tras indicar tu correo electrónico.',
  cg_gate_domains_hint: 'Disponible para direcciones {domains}',
  cg_email: 'Correo electrónico',
  cg_first_name: 'Nombre',
  cg_last_name: 'Apellidos',
  cg_company: 'Empresa',
  cg_continue: 'Continuar',
  cg_login_required: 'Inicia sesión para ver este contenido.',
  cg_sso_required: 'Inicia sesión con tu organización para ver este contenido.',
  cg_share_required: 'Este contenido solo está disponible mediante un enlace compartido.',
  cg_private: 'Este contenido no está disponible.',
  cg_embargoed: 'Disponible el {date}.',
  cg_embargoed_soon: 'Aún no disponible.',
  cg_go_to_gallery: 'Abrir en la galería',
  cg_not_found: 'No hay ninguna galería de contenidos publicada para este sitio.',
  cg_kind_video: 'Vídeo',
  cg_kind_clip: 'Clip',
  cg_kind_podcast: 'Pódcast',
  cg_kind_article: 'Artículo',
  cg_kind_whitepaper: 'Libro blanco',
  cg_kind_document: 'Documento',
  cg_kind_transcript: 'Transcripción',
  cg_kind_session: 'Sesión',
  cg_kind_lesson: 'Lección',
  cg_kind_link: 'Enlace',

  disclosure_none: 'Sin relaciones financieras declaradas',
  disclosure_count_one: '{count} relación financiera declarada',
  disclosure_count_other: '{count} relaciones financieras declaradas',
  disclosure_list_label: 'Relaciones financieras declaradas',

  // Advocacy "I'm attending" card + open-link signup (advocacy-attending)
  aa_loading: 'Cargando tu tarjeta para compartir…',
  aa_event_fallback: 'el evento',
  aa_title_named: '{name}, vas a {event}',
  aa_title: 'Vas a {event}',
  aa_subtitle: 'Cuéntaselo a tu red: comparte tu tarjeta.',
  aa_image_alt_named: 'Tarjeta para compartir: {name} asiste a {event}',
  aa_image_alt: 'Tarjeta para compartir: asistencia a {event}',
  aa_share_group: 'Comparte tu tarjeta',
  aa_new_tab: '(se abre en una pestaña nueva)',
  aa_share_subject: 'Voy a {event}',
  aa_channel_email: 'Correo',
  aa_copy_link: 'Copiar enlace',
  aa_copied: 'Copiado',
  aa_copied_announce: 'Enlace copiado al portapapeles.',
  aa_copy_failed: 'No se pudo copiar el enlace. Selecciónalo y cópialo: {url}',
  aa_make_it_yours: 'Personalízala',
  aa_make_it_yours_hint: 'Añade tu foto y elige tu tarjeta',
  aa_unavailable_registration: 'Tu tarjeta para compartir no está disponible para esta inscripción.',
  aa_unavailable_event: 'Las tarjetas para compartir no están disponibles para este evento en este momento.',
  aa_error_reload: 'No se pudo cargar tu tarjeta para compartir. Actualiza la página.',
  aa_signup_title: 'Cuéntale a tu red que vas a {event}',
  aa_signup_intro: 'Crea tu tarjeta personal para compartir en unos segundos.',
  aa_required_note: 'Los campos marcados con * son obligatorios.',
  aa_required: 'obligatorio',
  aa_field_first_name: 'Nombre',
  aa_field_last_name: 'Apellido',
  aa_field_email: 'Correo electrónico',
  aa_field_company: 'Empresa',
  aa_field_job_title: 'Cargo',
  aa_consent: 'Acepto recibir mi enlace para compartir y novedades sobre {event}.',
  aa_submit: 'Crear mi tarjeta',
  aa_submitting: 'Creando tu tarjeta…',
  aa_err_first_name: 'Escribe tu nombre.',
  aa_err_email: 'Escribe un correo electrónico válido, por ejemplo nombre@example.com.',
  aa_err_consent: 'Marca la casilla para obtener tu tarjeta para compartir.',
  aa_err_summary_one: 'Hay {count} problema en el formulario.',
  aa_err_summary_other: 'Hay {count} problemas en el formulario.',
  aa_err_generic: 'Algo salió mal. Inténtalo de nuevo.',
  aa_check_email_title: 'Revisa tu bandeja de entrada',
  aa_check_email_body:
    'Si {email} ya tiene una tarjeta para compartir de {event}, te hemos enviado allí el enlace personal.',
  aa_card_ready: 'Tu tarjeta para compartir está lista.',
  aa_preview_label: 'Vista previa de tu tarjeta para compartir',
};

export const DICTIONARIES = { en, de, fr, es };
